import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { trackEvent } from "@/utils/analytics";
import "./LiveDemo.scss";

interface LiveDemoProps {
  url: string;
  frame: "phone" | "browser";
  projectKey: string;
  projectName: string;
  liveUrl: string;
  posterSrc: string;
}

const LOAD_TIMEOUT_MS = 8000;

function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return false;
    }
    return window.matchMedia("(max-width: 768px)").matches ?? false;
  });
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(max-width: 768px)");
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return isMobile;
}

const ExternalIcon: React.FC = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    aria-hidden="true"
  >
    <path d="M7 3H3v10h10V9M10 2h4v4M14 2L8 8" />
  </svg>
);

const LiveDemo: React.FC<LiveDemoProps> = ({
  url,
  frame,
  projectKey,
  projectName,
  liveUrl,
  posterSrc,
}) => {
  const { t } = useTranslation();
  const [active, setActive] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const isMobile = useIsMobile();
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const loadedRef = useRef(false);
  const regionRef = useRef<HTMLDivElement>(null);

  const prefersReducedMotion =
    typeof window !== "undefined"
      ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
      : false;

  const handleRun = () => {
    if (isMobile) return;
    loadedRef.current = false;
    setActive(true);
    setLoaded(false);
    setFailed(false);
    trackEvent("case_study_demo_run", { project: projectKey });
    if (!prefersReducedMotion) {
      requestAnimationFrame(() => regionRef.current?.focus());
    }
    timeoutRef.current = setTimeout(() => {
      if (!loadedRef.current) {
        setActive(false);
        setFailed(true);
        trackEvent("case_study_demo_fallback", { project: projectKey });
      }
    }, LOAD_TIMEOUT_MS);
  };

  const handleLoad = () => {
    loadedRef.current = true;
    setLoaded(true);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  };

  const handleError = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setActive(false);
    setFailed(true);
    trackEvent("case_study_demo_fallback", { project: projectKey });
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const showIframe = active && !isMobile;

  return (
    <div
      className={`live-demo live-demo--${frame}`}
      ref={regionRef}
      tabIndex={-1}
      aria-label={`${projectName} live demo`}
    >
      <a
        href={liveUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="live-demo__external"
      >
        {t("caseStudy.openLiveApp")} <ExternalIcon />
      </a>

      {!showIframe && (
        <div className="live-demo__poster">
          <img
            src={posterSrc}
            alt={`${projectName} preview`}
            className="live-demo__poster-img"
          />
          {!isMobile && (
            <button
              type="button"
              className="live-demo__run-btn"
              onClick={handleRun}
              aria-label={t("caseStudy.runDemo")}
            >
              ▶ {t("caseStudy.runDemo")}
            </button>
          )}
          {failed && (
            <p className="live-demo__fallback-msg" role="status">
              {t("caseStudy.demoFallback")}
            </p>
          )}
        </div>
      )}

      {showIframe && (
        <div className={`live-demo__frame-wrap live-demo__frame-wrap--${frame}`}>
          {!loaded && (
            <div className="live-demo__loading" aria-live="polite">
              {t("caseStudy.demoLoading")}
            </div>
          )}
          <iframe
            src={url}
            title={`${projectName} live demo`}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
            onLoad={handleLoad}
            onError={handleError}
            className={`live-demo__iframe${loaded ? " live-demo__iframe--loaded" : ""}`}
          />
        </div>
      )}
    </div>
  );
};

export default LiveDemo;
